import React from 'react';
import { 
    Box, 
    Typography, 
    useTheme, 
    useMediaQuery,
    Stack,
    Paper,
    Divider
} from '@mui/material';
import { 
    BarChart3, 
    TrendingUp, 
    Download, 
    MoreHorizontal 
} from 'lucide-react';

const ReportHeroSection = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Sample data for the mock CSS bar chart
    const chartData = [
        { height: '40%', color: '#E2E8F0' },
        { height: '65%', color: '#E2E8F0' },
        { height: '45%', color: '#E2E8F0' },
        { height: '85%', color: '#4F46E5' }, // Highlighted active bar
        { height: '60%', color: '#E2E8F0' },
        { height: '100%', color: '#E2E8F0' },
    ];

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '100vw',
                overflowX: 'hidden',
                boxSizing: 'border-box',
                backgroundColor: '#F8FAFC',
                py: { xs: 8, md: 12 },
                display: 'flex',
                justifyContent: 'center',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '1280px',
                    mx: 'auto',
                    px: { xs: 3, md: 6 },
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row-reverse', // Alternates layout
                    alignItems: 'center',
                    gap: { xs: 8, md: 8 },
                }}
            >
                {/* Text Section (Right side on desktop, Top on mobile) */}
                <Box sx={{ flex: 1, textAlign: isMobile ? 'center' : 'left' }}>
                    
                    <Typography
                        variant="h1"
                        sx={{ 
                            color: '#0F172A',
                            fontSize: { xs: '2.5rem', sm: '3.5rem', lg: '4rem' }, 
                            fontWeight: 800, 
                            lineHeight: 1.1,
                            letterSpacing: '-0.03em',
                            mb: 4
                        }}
                    >
                        Run a <Box component="span" sx={{ color: '#4F46E5' }}>report</Box> <br />
                        in seconds
                    </Typography>

                    <Stack 
                        direction={{ xs: 'column', md: 'row' }} 
                        spacing={3} 
                        alignItems={{ xs: 'center', md: 'flex-start' }}
                    >

                        {/* Description Text */}
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                color: '#475569', 
                                fontSize: '1.125rem',
                                lineHeight: 1.7,
                                maxWidth: '500px',
                            }}
                        >
                            Gain a deeper understanding of your business performance with a wide range of insightful, ready-to-use reports designed to help you make smarter financial decisions.
                        </Typography>
                    </Stack>
                </Box>

                {/* Mock UI Section (Left side on desktop, Bottom on mobile) */}
                <Box sx={{ 
                    flex: 1, 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    position: 'relative',
                    width: '100%',
                    height: { xs: '450px', md: '550px' }
                }}>
                    
                    {/* Abstract Background Blob (Purple to Indigo for "Growth") */}
                    <Box
                        sx={{
                            position: 'absolute',
                            height: { xs: '300px', sm: '400px' },
                            width: { xs: '300px', sm: '400px' },
                            background: 'linear-gradient(135deg, #DDD6FE 0%, #E0E7FF 100%)', 
                            borderRadius: '50% 50% 30% 70% / 70% 30% 70% 30%', 
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%) rotate(15deg)',
                            zIndex: 0,
                            opacity: 0.8,
                            filter: 'blur(20px)'
                        }}
                    />

                    {/* Main Mock Chart Card */}
                    <Paper
                        elevation={0}
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            width: '100%',
                            maxWidth: '360px',
                            backgroundColor: '#ffffff',
                            borderRadius: '24px',
                            p: 3,
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.1)',
                            border: '1px solid rgba(226, 232, 240, 0.8)',
                        }}
                    >
                        {/* Header */}
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography sx={{ fontSize: '1.1rem', color: '#0F172A', fontWeight: 700 }}>
                                Q3 Performance
                            </Typography>
                            <MoreHorizontal size={20} color="#94A3B8" />
                        </Stack>
                        <Typography sx={{ fontSize: '0.85rem', color: '#64748B', mb: 3 }}>
                            Revenue vs Expenses
                        </Typography>

                        {/* CSS Bar Chart */}
                        <Stack 
                            direction="row" 
                            justifyContent="space-between" 
                            alignItems="flex-end" 
                            sx={{ height: '140px', mb: 3 }}
                        >
                            {chartData.map((bar, index) => (
                                <Box key={index} sx={{ width: '12%', height: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                                    <Box 
                                        sx={{ 
                                            width: '100%', 
                                            height: bar.height, 
                                            backgroundColor: bar.color, 
                                            borderRadius: '6px',
                                            transition: 'height 0.5s ease'
                                        }} 
                                    />
                                </Box>
                            ))}
                        </Stack>

                        <Divider sx={{ mb: 3, borderColor: '#F1F5F9' }} />

                        {/* Chart Details Row */}
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box>
                                <Typography sx={{ fontSize: '0.75rem', color: '#64748B', mb: 0.5 }}>Net Profit</Typography>
                                <Typography sx={{ fontSize: '1.25rem', color: '#0F172A', fontWeight: 800 }}>LKR.24,850</Typography>
                            </Box>
                            <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1, 
                                bgcolor: '#F8FAFC', 
                                px: 1.5, 
                                py: 1, 
                                borderRadius: '10px',
                                border: '1px solid #E2E8F0',
                                color: '#475569',
                                fontSize: '0.85rem',
                                fontWeight: 600
                            }}>
                                <Download size={14} /> Export
                            </Box>
                        </Stack>
                    </Paper>

                    {/* Floating Growth Highlight Card */}
                    <Paper
                        elevation={0}
                        sx={{
                            position: 'absolute',
                            top: { xs: '5%', md: '10%' },
                            left: { xs: '0%', sm: '5%', md: '-5%' },
                            zIndex: 2,
                            backgroundColor: '#ffffff',
                            borderRadius: '16px',
                            p: 2,
                            boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.12)',
                            border: '1px solid rgba(226, 232, 240, 0.8)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            width: '200px'
                        }}
                    >
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            bgcolor: '#ECFDF5', 
                            color: '#10B981', 
                            width: 36, 
                            height: 36, 
                            borderRadius: '50%' 
                        }}>
                            <TrendingUp size={20} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>Growth</Typography>
                            <Typography sx={{ fontSize: '1rem', color: '#0F172A', fontWeight: 700 }}>+24.5%</Typography>
                        </Box>
                    </Paper>

                </Box>
            </Box>
        </Box>
    );
};

export default ReportHeroSection;